import { KeyboardEvent } from 'react';

import { cn } from '@/lib/utils';

import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import type { ColumnDef } from './columns';
import { ISO_DATE, dateToIso, isoToDate } from './customFields';

/** Attributes for the focusable control: its id, aria wiring and the grid's cell marker. */
type ControlProps = Record<string, unknown>;

const UNSET = '__unset__';
const TRI_STATE = { yes: true, no: false } as const;

/**
 * Keys Radix's select opens on. In the grid they would also move the active
 * cell, so they stop here; Tab and the left/right arrows still navigate.
 */
function keepOpenKeysInside(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.stopPropagation();
    }
}

function ChoiceSelect({
    value,
    options,
    allowUnset,
    onChange,
    compact,
    controlProps,
    testId,
}: {
    value: string;
    options: { value: string; label: string }[];
    allowUnset: boolean;
    onChange: (value: string) => void;
    compact: boolean;
    controlProps: ControlProps;
    testId?: string;
}) {
    return (
        <Select
            value={value}
            onValueChange={(v) => onChange(v === UNSET ? '' : v)}
        >
            <SelectTrigger
                {...controlProps}
                data-testid={testId}
                onKeyDown={keepOpenKeysInside}
                className={cn(compact ? 'h-8 min-w-32 text-sm' : 'w-full')}
            >
                <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
                {allowUnset && <SelectItem value={UNSET}>Not set</SelectItem>}
                {options.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                        {o.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function BooleanInput({
    field,
    value,
    onChange,
    compact,
    controlProps,
    testId,
}: Props) {
    // Three-state so "not answered" can be told from "no".
    const selected = value === true ? 'yes' : value === false ? 'no' : '';
    return (
        <ChoiceSelect
            value={selected}
            allowUnset
            options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
            ]}
            onChange={(v) =>
                onChange(
                    v === '' ? null : TRI_STATE[v as keyof typeof TRI_STATE],
                )
            }
            compact={compact}
            controlProps={controlProps}
            testId={testId}
        />
    );
}

interface Props {
    field: ColumnDef;
    value: unknown;
    onChange: (value: unknown) => void;
    /** Grid-cell sizing instead of form sizing. */
    compact: boolean;
    controlProps: ControlProps;
    testId?: string;
}

/**
 * The input for one custom field, chosen by its kind: text, long text, integer,
 * decimal, boolean, date or enum. Unset is `null`; a decimal is kept as the
 * string typed (so no float rounding) and a date as `YYYY-MM-DD`. Shared by the
 * header form and the row grid; each caller supplies the label, help text and error.
 */
export function CustomFieldInput(props: Props) {
    const { field, value, onChange, compact, controlProps, testId } = props;
    const inputClass = compact ? 'h-8 text-sm min-w-32' : undefined;
    const text = value == null ? '' : String(value);

    if (field.kind === 'long_text' && !compact) {
        return (
            <Textarea
                {...controlProps}
                data-testid={testId}
                value={text}
                onChange={(e) =>
                    onChange(e.target.value === '' ? null : e.target.value)
                }
            />
        );
    }

    switch (field.kind) {
        case 'bool':
            return <BooleanInput {...props} />;
        case 'enum':
            return (
                <ChoiceSelect
                    value={text}
                    allowUnset
                    options={(field.choices ?? []).map((c) => ({
                        value: c,
                        label: c,
                    }))}
                    onChange={(v) => onChange(v === '' ? null : v)}
                    compact={compact}
                    controlProps={controlProps}
                    testId={testId}
                />
            );
        case 'date':
            return (
                <DatePicker
                    date={isoToDate(value)}
                    dateFormat={ISO_DATE}
                    onDateChange={(d) => onChange(d ? dateToIso(d) : null)}
                    className={compact ? 'w-40!' : 'w-full max-w-[280px]!'}
                    inputClassName={compact ? 'h-8 text-sm' : undefined}
                    isError={controlProps['aria-invalid'] === true}
                    inputProps={controlProps}
                    testId={testId}
                />
            );
        case 'integer':
        case 'decimal':
            return (
                <Input
                    {...controlProps}
                    data-testid={testId}
                    type="number"
                    step={field.kind === 'integer' ? 1 : 'any'}
                    value={text}
                    className={cn(inputClass, compact && 'w-28 min-w-0')}
                    onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') onChange(null);
                        else onChange(field.kind === 'integer' ? Number(v) : v);
                    }}
                />
            );
        default:
            // text, and long text inside a grid cell (one line)
            return (
                <Input
                    {...controlProps}
                    data-testid={testId}
                    value={text}
                    className={inputClass}
                    onChange={(e) =>
                        onChange(e.target.value === '' ? null : e.target.value)
                    }
                />
            );
    }
}
